import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import VideoPlayer from "@/components/video/VideoPlayer";
import VideoVerificationPanel from "@/components/video/VideoVerificationPanel";
import { useVideoVerification } from "@/hooks/useVideoVerification";
import { openVideoPopout } from "@/lib/videoWindow";
import {
  VIDEO_CHANNELS,
  VIDEO_LANES,
  getVideoChannel,
  type VideoLane,
} from "@shared/videoChannels";
import { ExternalLink, Maximize2, Radio, Tv2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useRoute, useSearchParams } from "wouter";

const DEFAULT_CHANNEL_SLUG = "bloomberg";

export default function Video() {
  const [, params] = useRoute<{ channelSlug: string }>("/video/:channelSlug");
  const [, setLocation] = useLocation();
  const [searchParams] = useSearchParams();
  const asOf = searchParams.has("asOf") ? searchParams.get("asOf") : null;
  const requestedSlug = params?.channelSlug;
  const requestedChannel = requestedSlug ? getVideoChannel(requestedSlug) : undefined;
  const selectedChannel = requestedChannel ?? getVideoChannel(DEFAULT_CHANNEL_SLUG)!;
  const unknownChannel = Boolean(requestedSlug && !requestedChannel);
  const channelId = `youtube:${selectedChannel.youtubeChannelId}` as const;
  const verification = useVideoVerification(asOf, channelId);
  const [activeLane, setActiveLane] = useState<VideoLane | "All">("All");
  const [popupMessage, setPopupMessage] = useState("");
  const playerHeadingRef = useRef<HTMLElement | null>(null);
  const priorSlugRef = useRef(selectedChannel.slug);

  useEffect(() => {
    if (priorSlugRef.current !== selectedChannel.slug) {
      playerHeadingRef.current = document.getElementById("video-player-title");
      playerHeadingRef.current?.focus();
      priorSlugRef.current = selectedChannel.slug;
    }
  }, [selectedChannel.slug]);

  const filteredChannels = useMemo(
    () => activeLane === "All"
      ? VIDEO_CHANNELS
      : VIDEO_CHANNELS.filter((channel) => channel.categories.includes(activeLane)),
    [activeLane],
  );

  const canonicalPath = requestedChannel ? `/video/${requestedChannel.slug}` : "/video";
  const historicalRecord = verification.view?.channels.find((record) => record.channelId === channelId);
  const pageTitle = unknownChannel
    ? "Video channel not found | Tylee Smart Markets"
    : asOf !== null
      ? `${historicalRecord?.channel.name ?? "Historical channel"} Playback Verification | Tylee Smart Markets`
    : requestedChannel
      ? `${selectedChannel.name} Live Video | Tylee Smart Markets`
      : "Live Business & Global News TV | Tylee Smart Markets";
  const description = unknownChannel
    ? "The requested broadcaster is not in the verified Tylee Smart Markets video roster."
    : asOf !== null
      ? "Dated channel identity and playback checks retained by a UTC boundary. Historical media is not retained."
    : requestedChannel
      ? `${selectedChannel.description} Open the current live player and dated playback checks separately.`
      : "Watch live business, financial, global news, weather, and breaking-news coverage from Tylee Smart Markets.";

  function selectChannel(slug: string) {
    setPopupMessage("");
    setLocation(`/video/${slug}${asOf !== null ? `?asOf=${encodeURIComponent(asOf)}` : ""}`);
  }

  function popOut() {
    if (asOf !== null) {
      const opened = window.open(
        `/video/popout/${selectedChannel.slug}?asOf=${encodeURIComponent(asOf)}`,
        "tsm-video-player",
        "popup=yes,width=960,height=600,resizable=yes,scrollbars=yes",
      );
      if (opened) {
        try { opened.opener = null; } catch { /* Same-origin route remains usable. */ }
        opened.focus();
      }
      setPopupMessage(opened ? "Historical verification opened in the TSM video window." : "The pop-out was blocked. Allow pop-ups for TSM and try again.");
      return;
    }
    const result = openVideoPopout(selectedChannel.slug);
    setPopupMessage(result.status === "blocked"
      ? "The pop-out was blocked. Allow pop-ups for TSM and try again."
      : result.status === "reused"
        ? "The existing TSM video window was updated."
        : "Video opened in a reusable TSM window.");
  }

  return (
    <div className="tsm-home-shell theme-amber video-shell">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://tyl.ee${canonicalPath}`} />
        {(unknownChannel || asOf !== null) && <meta name="robots" content="noindex, nofollow" />}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://tyl.ee${canonicalPath}`} />
        <meta property="og:image" content="https://tyl.ee/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <AppHeader brandText="Video" />

      <main className="video-main">
        <section className="video-lede" aria-labelledby="video-page-title">
          <div>
            <p className="home-kicker">{asOf === null ? "LIVE VIDEO NEWSROOM" : "DATED PLAYBACK VERIFICATION"}</p>
            <h1 id="video-page-title">{asOf === null ? "One signal. One channel. No video wall." : "What was retained by this time?"}</h1>
            <p>Choose a current live player inside TSM, one channel at a time. Dated playback checks are shown separately and do not prove current playback.</p>
          </div>
          <nav aria-label="Video newsroom links">
            <Link href="/monitor">Monitor</Link>
            <Link href="/maps">Maps</Link>
            <Link href="/analytics">Analytics</Link>
          </nav>
        </section>

        {unknownChannel ? (
          <section className="video-not-found" aria-labelledby="video-not-found-title">
            <Tv2 size={30} aria-hidden="true" />
            <h2 id="video-not-found-title">Channel not found</h2>
            <p>This URL is not part of the verified permanent broadcaster roster.</p>
            <Link href="/video">Open the Video newsroom</Link>
          </section>
        ) : (
          <>
            <div className="video-primary" ref={(node) => { playerHeadingRef.current = node?.querySelector("#video-player-title") ?? null; }}>
              {asOf === null ? (
                <>
                  <VideoPlayer channel={selectedChannel} />
                  <div className="video-primary-actions">
                    <button type="button" onClick={popOut}>
                      <Maximize2 size={15} aria-hidden="true" /> Pop out
                    </button>
                    <a href={selectedChannel.sourceUrl} target="_blank" rel="noreferrer">
                      <ExternalLink size={15} aria-hidden="true" /> Official source
                    </a>
                    <span role="status" aria-live="polite">{popupMessage}</span>
                  </div>
                </>
              ) : (
                <div className="video-historical-actions">
                  <p className="home-kicker">HISTORICAL VERIFICATION VIEW</p>
                  <h2>Live video is hidden for a historical boundary.</h2>
                  <button type="button" onClick={popOut}><Maximize2 size={15} aria-hidden="true" /> Open historical view</button>
                </div>
              )}
              <VideoVerificationPanel
                asOf={asOf}
                channelId={channelId}
                status={verification.status}
                view={verification.view}
                onApplyBoundary={(boundary) => setLocation(`/video/${selectedChannel.slug}${boundary ? `?asOf=${encodeURIComponent(boundary)}` : ""}`)}
              />
            </div>

            <section className="video-directory" aria-labelledby="video-directory-title">
              <div className="video-directory-head">
                <div>
                  <p className="home-kicker">{asOf === null ? "PERMANENT CHANNELS" : "CURRENT ROSTER NAVIGATION"}</p>
                  <h2 id="video-directory-title">{asOf === null ? "Broadcaster directory" : "Current channel navigation"}</h2>
                </div>
                <div className="video-filters" role="group" aria-label="Filter video channels">
                  {(["All", ...VIDEO_LANES] as const).map((lane) => (
                    <button
                      key={lane}
                      type="button"
                      aria-pressed={activeLane === lane}
                      onClick={() => setActiveLane(lane)}
                    >
                      {lane}
                    </button>
                  ))}
                </div>
              </div>

              <div className="video-channel-grid" role="list" aria-label="Video channels">
                {filteredChannels.map((channel) => (
                  <article
                    key={channel.slug}
                    className={`video-channel-tile${channel.slug === selectedChannel.slug ? " is-selected" : ""}`}
                    role="listitem"
                  >
                    <button
                      type="button"
                      aria-pressed={channel.slug === selectedChannel.slug}
                      onClick={() => selectChannel(channel.slug)}
                    >
                      <span><Radio size={12} aria-hidden="true" /> Current roster</span>
                      <strong>{channel.name}</strong>
                      <small>{channel.categories.join(" · ")} · {channel.region}</small>
                      <p>{channel.description}</p>
                    </button>
                    <a href={channel.sourceUrl} target="_blank" rel="noreferrer" aria-label={`Open ${channel.name} official source`}>
                      Source <ExternalLink size={12} aria-hidden="true" />
                    </a>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
